import ROUTE from '@us/app/router/appModule/names'
import { decodeQuery } from '@shared/utils.js'
import SIGNUP_METHODS from '@us/app/config/signup-methods.js'
import VERIFICATION_METHODS from '@us/app/config/verification-methods.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import usePersistedConfigStore from '@shared/stores/usePersistedConfigStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useUsStore from '@us/app/stores/useUsStore.js'
import * as apiRequest from '@shared/api-endpoints'
import useSharedStore from '@shared/stores/useSharedStore.js'

const defaultSignUpMethod = SIGNUP_METHODS.VOUCHER

const preferredSignUpMethodMap = {
  [SIGNUP_METHODS.VOUCHER]: ROUTE.VERIFY_WITH_VOUCHER,
  [SIGNUP_METHODS.UHCHUB]: ROUTE.VERIFY_WITH_UHCHUB,
  [SIGNUP_METHODS.UHCSTORE]: ROUTE.VERIFY_WITH_UHCSTORE,
  [SIGNUP_METHODS.WEBMD_ONE]: ROUTE.VERIFY_WITH_WEBMD_ONE,
  [SIGNUP_METHODS.ELIGIBILITY_LIST]: ROUTE.VERIFY_WITH_ELIGIBILITY_LIST,
  [SIGNUP_METHODS.SOLERA]: ROUTE.VERIFY_WITH_SOLERA,
  [SIGNUP_METHODS.PERSONIFY_HEALTH_SSO]: ROUTE.SAML_SSO_INITIATED,
}

const getCorporateKeyFromRoute = (route) =>
  (route.params.corporate_key || route.query.corporate || '').toLowerCase()

export default async (routeTo) => {
  const authStore = useAuthStore()
  const persistedConfigStore = usePersistedConfigStore()
  const userStore = useUserStore()
  const usStore = useUsStore()
  const sharedStore = useSharedStore()

  const redirectTo = (name, qs, params) => ({
    name,
    query: qs || routeTo.query,
    params: params || routeTo.params,
  })

  let corporateKey = getCorporateKeyFromRoute(routeTo)

  // authenticate with one_time_token if available
  if (routeTo.query.one_time_token) {
    try {
      await apiRequest.loginWithOneTimeAuthToken(routeTo.query.one_time_token)
    } catch (e) {
      console.warn(
        'authentication with one_time_token failed, falling back to user login',
        e,
      )
    }
    delete routeTo.query.one_time_token
    // reset the route so that the query gets updated
    return routeTo
  }

  sharedStore.storeInitialQuery(decodeQuery(routeTo.query))
  const initialQuery = sharedStore.initialQuery

  // handle routes that don't need a corporate object
  switch (routeTo.name) {
    case ROUTE.DEEPLINK_FALLBACK:
    case ROUTE.PASSWORD_RESET:
    case ROUTE.PARTNER_SEARCH:
    case ROUTE.LANDING_PAGE_GENERIC:
    case ROUTE.INIT_UHCHUB:
    case ROUTE.INIT_WEBMD_ONE:
    case ROUTE.INIT_PERSONIFY_HEALTH:
      return true
  }

  let corporate = null
  try {
    corporate = await apiRequest.loadCorporate({ key: corporateKey })
  } catch {
    return redirectTo(
      ROUTE.PARTNER_SEARCH,
      { unresolved_corporate_key: corporateKey },
      {},
    )
  }

  if (!corporate.isCustomer) {
    // TODO pass corporate key to partner search and show not customer warning there
    return redirectTo(
      ROUTE.PARTNER_SEARCH,
      { unresolved_corporate_key: corporate.key },
      {},
    )
  }

  // handle routes that need a corporate object
  switch (routeTo.name) {
    case ROUTE.LANDING_PAGE_BRANDED: {
      // skip the information screen if the corporate has disabled extended onboarding
      if (!corporate.showExtendedOnboarding) {
        return redirectTo(ROUTE.SELECT_VERIFICATION_METHOD)
      }

      // skip the information screen if its a login flow
      if (
        initialQuery.login === 'true' ||
        initialQuery.skip_information ||
        authStore.isAuthenticated
      ) {
        return redirectTo(
          ROUTE.SELECT_VERIFICATION_METHOD,
          {},
          {
            corporate_key: corporateKey,
          },
        )
      }

      // decide whether to show the incentive banner
      persistedConfigStore.assignIncentiveVariant(corporate)
      break
    }

    case ROUTE.SELECT_VERIFICATION_METHOD: {
      let voucherCode = initialQuery.voucher_code || initialQuery.voucher
      const email = initialQuery.email

      let signUpMethod = corporate.preferredSignupMethod
      if (!Object.values(SIGNUP_METHODS).includes(signUpMethod)) {
        console.error(
          new Error(
            `Invalid preferred signup method ${signUpMethod} for corporate ${corporate.key}, falling back to ${defaultSignUpMethod}`,
          ),
        )

        signUpMethod = defaultSignUpMethod
      }

      return {
        ...routeTo,
        name: preferredSignUpMethodMap[signUpMethod],
        params: {
          corporate_key: corporate.key,
          ...(signUpMethod === SIGNUP_METHODS.VOUCHER &&
            voucherCode && { voucherCode }),
        },
        query: {
          ...routeTo.query,
          ...(email && { email }),
        },
      }
    }

    case ROUTE.SAML_SSO_INITIATED:
      if (userStore.user) {
        return redirectTo(ROUTE.SAML_SSO_COMPLETED)
      }
      break

    case ROUTE.SAML_SSO_COMPLETED:
      if (!authStore.isAuthenticated) {
        return redirectTo(ROUTE.SAML_SSO_ERROR, { code: 'UNAUTHORIZED' })
      }

      // Block the user from going any further without a valid subscription.
      // This should be removed as soon as subscriptions are handled correctly in the apps.
      if (!userStore.user?.hasActiveSubscription) {
        return redirectTo(ROUTE.SAML_SSO_ERROR, {
          code: 'NO_ACTIVE_SUBSCRIPTION',
        })
      }

      if (userStore.user?.profile?.hasAcceptedDataProtection) {
        return redirectTo(ROUTE.DOWNLOAD_LINK)
      }
      break

    case ROUTE.SIGN_UP: {
      if (authStore.isAuthenticated) {
        return redirectTo(ROUTE.SUBSCRIPTION)
      }

      if (initialQuery.login === 'true') {
        return redirectTo(ROUTE.LOGIN)
      }

      if (
        !Object.values(VERIFICATION_METHODS).includes(
          usStore.corporateEligibilityData.type,
        )
      ) {
        return redirectTo(ROUTE.SELECT_VERIFICATION_METHOD)
      }
      break
    }

    case ROUTE.LOGIN:
      if (authStore.isAuthenticated) {
        return redirectTo(ROUTE.SUBSCRIPTION)
      }
      break

    case ROUTE.SUBSCRIPTION:
      if (!authStore.isAuthenticated) {
        return redirectTo(ROUTE.SIGN_UP)
      }

      if (
        !Object.values(VERIFICATION_METHODS).includes(
          usStore.corporateEligibilityData.type,
        )
      ) {
        return redirectTo(ROUTE.SELECT_VERIFICATION_METHOD)
      }
      break

    case ROUTE.DOWNLOAD_LINK:
      if (!authStore.isAuthenticated) {
        return redirectTo(ROUTE.LOGIN)
      }
      break

    default:
      break
  }
}
